import {
  Autocomplete,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useInscripcionContext } from "../../inscripcion-context";
import { useStepperContext } from "app/contexts/stepper";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useResourcesContext } from "app/contexts/resources";

const initialState = {
  actividad_economica_id: 0,
  actividad_economica_detalles: "",
  actividad_economica_principal: false,
};

function ActividadEconomicaForm({ actividad_economica = initialState }) {
  const { actividadesEconomicas } = useResourcesContext();

  const [options] = useState(() =>
    actividadesEconomicas.map((actividad_economica) => ({
      label: `${actividad_economica.actividad_economica_id} - ${actividad_economica.actividad_economica_descripcion}`,
      ...actividad_economica,
    }))
  );

  const {
    addActividadEconomica,
    updateActividadEconomica,
    deleteActividadEconomica,
  } = useInscripcionContext();

  const [state, setState] = useState(actividad_economica);
  const [isEditable] = useState(!!actividad_economica.actividad_id);
  const [isEditing, setEditing] = useState(false);

  const [value, setValue] = useState(null);

  useEffect(() => {
    const option = options.find(
      (option) => option.actividad_economica_id === state.actividad_economica_id
    );

    setValue(option || null);
  }, [options, state.actividad_economica_id]);

  function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!!isEditable && !isEditing) {
        return setEditing(true);
      } else if (!!isEditable && !!isEditing) {
        const isUpdated = updateActividadEconomica({
          actividad_economica: state,
        });

        if (isUpdated) {
          setEditing(false);

          return toast.success("¡Actividad económica actualizada!");
        }
      } else {
        const isAdded = addActividadEconomica({ actividad_economica: state });

        if (isAdded) {
          setState(actividad_economica);

          return toast.success("¡Actividad económica agregada!");
        }
      }
    } catch (error) {
      console.log(error.message);

      return toast.error(error.message);
    }
  }

  function handleReset() {
    if (!!isEditable && !isEditing) {
      const isDeleted = deleteActividadEconomica({
        actividad_id: actividad_economica.actividad_id,
      });

      if (isDeleted) {
        return toast.success("¡Actividad económica eliminada!");
      }
    } else if (!!isEditable && !!isEditing) {
      const isCanceled = window.confirm("¿Desea cancelar la edición?");

      if (isCanceled) {
        setState(actividad_economica);

        return setEditing(false);
      }
    } else {
      const isReseted = window.confirm("¿Desea reiniciar el formulario?");

      if (isReseted) {
        setState(actividad_economica);
      }
    }
  }

  return (
    <Paper
      component="form"
      className="animate__animated animate__slideInUp"
      onSubmit={handleSubmit}
      onReset={handleReset}
      variant="outlined"
    >
      <Grid container spacing={1} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <Typography variant="overline">
            <strong>{`${
              !!isEditable ? "Editar" : "Agregar"
            } actividad económica`}</strong>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={value}
            options={options}
            renderInput={(params) => (
              <TextField {...params} label="Actividad económica" required />
            )}
            onChange={(e, value) => {
              setValue(value);

              setState((state) => ({
                ...state,
                actividad_economica_id: value?.actividad_economica_id || 0,
              }));
            }}
            disabled={!!isEditable && !isEditing}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            type="text"
            label="Detalles"
            value={state.actividad_economica_detalles}
            onChange={(e) =>
              setState((state) => ({
                ...state,
                actividad_economica_detalles: e.target.value,
              }))
            }
            size="small"
            fullWidth
            multiline
            rows={2}
            required
            disabled={!!isEditable && !isEditing}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.actividad_economica_principal}
                  onChange={(e) =>
                    setState((state) => ({
                      ...state,
                      actividad_economica_principal: e.target.checked,
                    }))
                  }
                />
              }
              label="¿Es un actividad económica principal?"
              disabled={!!isEditable && !isEditing}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack direction="row" justifyContent="end" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              color={!!isEditable ? "warning" : "success"}
            >
              {!!isEditable && !isEditing
                ? "Editar"
                : !!isEditing
                ? "Actualizar"
                : "Agregar"}
            </Button>

            <Button
              type="reset"
              variant="outlined"
              size="small"
              color={!!isEditable && !isEditing ? "error" : "inherit"}
            >
              {!!isEditable && !isEditing
                ? "Eliminar"
                : !!isEditing
                ? "Cancelar"
                : "Reiniciar"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

export function ActividadesEconomicas() {
  const stepper = useStepperContext();
  const { empresa } = useInscripcionContext();

  function handleNext() {
    try {
      if (!empresa.actividades_economicas.length) {
        throw new Error(
          "¡Por favor, agregue al menos una actividad económica!"
        );
      }

      return stepper.next();
    } catch (error) {
      return toast.error(error.message);
    }
  }

  return (
    <Stack spacing={2}>
      <Container maxWidth="sm">
        <Stack spacing={2}>
          {empresa.actividades_economicas.map((actividad_economica) => (
            <ActividadEconomicaForm
              key={actividad_economica.actividad_id}
              actividad_economica={actividad_economica}
            />
          ))}

          <ActividadEconomicaForm />
        </Stack>
      </Container>

      <Stack
        direction="row"
        justifyContent="end"
        alignItems="center"
        spacing={1}
      >
        {!stepper.isFirstStep && (
          <Button variant="outlined" size="small" onClick={stepper.back}>
            Atrás
          </Button>
        )}

        <Button variant="contained" size="small" onClick={handleNext}>
          Siguiente
        </Button>
      </Stack>
    </Stack>
  );
}
